/* System defined core library */
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { RoutingService } from '../../shared/services/routing.service';
import { UtilityService } from '../../shared/services/utility.service';
import { SelectedExamDetailService } from '../shared/services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../shared/models/scheduling/appointmentdetail.model';
import { BatteryConfig } from '../../shared/models/scheduling/batteryConfig.model';
import { Client } from '../../shared/models/scheduling/client.model';
import { Country } from '../../shared/models/scheduling/country.model';
import { Exam } from '../../shared/models/scheduling/exam.model';
import { Program } from '../../shared/models/scheduling/program.model';
import { State } from '../../shared/models/scheduling/state.model';

/* User defined enums/constants */
import { ComponentType } from '../../shared/enums/scheduling.enum';


@Component({
  selector: 'app-find-availability',
  templateUrl: './find-availability.component.html'
})
export class FindAvailabilityComponent implements OnInit {

  /* User defined private/public variables  */
  public allNearbyCenters: boolean = false;
  public batteryConfig: BatteryConfig;
  public canNavigate: boolean = false;
  public durationError: boolean = false;
  public formattedAddress: string;
  public isExamContextValid: boolean = false;
  public isGoogleMapsValid: boolean = true;
  public isLocationSelected: boolean = false;
  public isRegionSelectorValid: boolean = false;
  public pathLock = false;
  public region: State;
  public selectedAppointments: AppointmentDetail[];
  public selectedClient: Client;
  public selectedCountry: Country;
  public selectedExam: Exam;
  public selectedLocation: any;
  public selectedProgram: Program;
  public selectedState: State;
  public totalApplicableTime: number = 690;

  /* Input-OutPut parameters decalartion */
  @Input() locationChange: boolean = false;

  /* Service subscriptions */
  allNearbyCenters$: Subscription;
  batteryConfig$: Subscription;
  client$: Subscription;
  country$: Subscription;
  exam$: Subscription;
  program$: Subscription;
  region$: Subscription;
  selectedLocation$: Subscription;
  state$: Subscription;
  translateLanguage$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private chRef: ChangeDetectorRef,
    private routingService: RoutingService,
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService,
    private utilityService: UtilityService
  ) {

    this.batteryConfig$ = selectedExamDetailService.batteryConfig.subscribe(
      batteryConfig => {
        this.batteryConfig = batteryConfig;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);

    this.isGoogleMapsValid = this.selectedExamDetailService.isGoogleMapsValid;

    this.client$ = selectedExamDetailService.client.subscribe(client => {
      this.selectedClient = client;
    });

    this.program$ = selectedExamDetailService.program.subscribe(program => {
      this.selectedProgram = program;
    });

    this.exam$ = selectedExamDetailService.exam.subscribe(exam => {
      this.selectedExam = exam;
    });

    this.region$ = selectedExamDetailService.region.subscribe(region => {
      this.region = region;
    });

    this.country$ = selectedExamDetailService.lpiCountry.subscribe(country => {
      this.selectedCountry = country;
    });

    this.state$ = selectedExamDetailService.lpiState.subscribe(state => {
      this.selectedState = state;
    });

    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(languageCode => {
      translate.use(languageCode);
    });
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit() {
    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.FindAvailabilityComponent)) return;
    this.pathLock = this.selectedExamDetailService.setSeatAvailabilityPathLock;
    this.isLocationSelected = (this.isGoogleMapsValid ? !!this.region : !!this.selectedCountry && !!this.selectedState);
    this.formattedAddress = (this.isLocationSelected ? this.region.name : "locationSelectorComponent.placeholderText");
  }

  /* Show Location selector screen if location is changed*/
  changeLocation(isLocationChanged: boolean) {
    this.locationChange = isLocationChanged;
  }

  /* Show Location selector screen if location is changed*/
  setSelectedExamContext(examContext: any) {
    this.selectedClient = examContext.client;
    this.selectedProgram = examContext.program;
    this.selectedExam = examContext.exam;

    this.selectedExamDetailService.setClient(this.selectedClient);
    this.selectedExamDetailService.setProgram(this.selectedProgram);
    this.selectedExamDetailService.setExam(this.selectedExam);

    let tempAppointmentDetail: AppointmentDetail = new AppointmentDetail();
    tempAppointmentDetail.exam = this.selectedExam;
    tempAppointmentDetail.program = this.selectedProgram;
    if (this.selectedExam != null) {
      tempAppointmentDetail.examName = this.selectedExam.name;
    }
    tempAppointmentDetail.client = this.selectedClient;
    this.selectedAppointments = [];
    this.selectedAppointments.push(tempAppointmentDetail);
    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

    this.isExamContextValid = !!examContext.client && !!examContext.program && !!examContext.exam;
  }

  duration(totalExamDuration: number) {
    this.durationError = !!this.batteryConfig ?
      totalExamDuration > this.batteryConfig.maxDuration :
      totalExamDuration > this.totalApplicableTime;
  }

  durationToStr() {
    return this.utilityService.durationToStr(!!this.batteryConfig ? this.batteryConfig.maxDuration : this.totalApplicableTime);
  }

  /* Show Location selector screen if location is changed*/
  submit(): void {
    this.routingService.searchFwdNavigation(ComponentType.FindAvailabilityComponent);
  }

  /* Show Location selector screen if location is changed*/
  goToPrevPage(): void {
    this.routingService.backwardNavigationSearch(ComponentType.FindAvailabilityComponent);
  }

  canProceed($event: any): void {
    this.canNavigate = $event;
    // Manually updating the page when setting selected appointments has completed because of an issue where the next button would not enable
    // until additional events updated the page again, despite conditions passing
    this.chRef.detectChanges();
  }

  /* if child component have selected exam(s) & max duration is not exceeded */
  /* Then the next btn is enabled, otherwise it's disabled. */
  isDisable(): boolean {
    return !this.canNavigate || this.durationError;
  }
}
